/**
 * Pure project transformation functions
 * 
 * NO I/O, NO async, NO side effects.
 * Functions for working with Project types.
 */

import type { Project, ProjectData, ProjectType, MindmapData, WhiteboardData, TextDocData } from '../models/project.js';

/**
 * Extracts the project type from a project.
 * Defaults to 'mindmap' if not specified.
 */
export const getProjectType = (project: Project): ProjectType => {
  if (project.jsonData.projectType === 'whiteboard') return 'whiteboard';
  if (project.jsonData.projectType === 'textdoc') return 'textdoc';
  return 'mindmap';
};

/**
 * Type guard: checks if a project is a whiteboard.
 */
export const isWhiteboard = (project: Project): boolean =>
  getProjectType(project) === 'whiteboard';

/**
 * Type guard: checks if a project is a mindmap.
 */
export const isMindmap = (project: Project): boolean =>
  getProjectType(project) === 'mindmap';

/**
 * Type guard: checks if a project is a text document.
 */
export const isTextDoc = (project: Project): boolean =>
  getProjectType(project) === 'textdoc';

/**
 * Type guard: checks if ProjectData is WhiteboardData.
 */
export const isWhiteboardData = (data: ProjectData): data is WhiteboardData =>
  data.projectType === 'whiteboard';

/**
 * Type guard: checks if ProjectData is TextDocData.
 */
export const isTextDocData = (data: ProjectData): data is TextDocData =>
  data.projectType === 'textdoc';

/**
 * Type guard: checks if ProjectData is MindmapData.
 */
export const isMindmapData = (data: ProjectData): data is MindmapData =>
  data.projectType !== 'whiteboard' && data.projectType !== 'textdoc';

/**
 * Creates an empty mindmap data structure.
 */
export const createEmptyMindmapData = (): MindmapData => ({
  projectType: 'mindmap',
  nodes: [],
});

/**
 * Creates an empty whiteboard data structure.
 */
export const createEmptyWhiteboardData = (): WhiteboardData => ({
  projectType: 'whiteboard',
  elements: [],
});

/**
 * Creates an empty text document data structure.
 */
export const createEmptyTextDocData = (): TextDocData => ({
  projectType: 'textdoc',
  content: null,
});

/**
 * Creates default project data based on type.
 */
export const createDefaultProjectData = (type: ProjectType): ProjectData => {
  if (type === 'whiteboard') return createEmptyWhiteboardData();
  if (type === 'textdoc') return createEmptyTextDocData();
  return createEmptyMindmapData();
};

/**
 * Gets the display label for a project type.
 */
export const getProjectTypeLabel = (type: ProjectType): string => {
  if (type === 'whiteboard') return 'Whiteboard';
  if (type === 'textdoc') return 'Document';
  return 'Mindmap';
};

/**
 * Checks if a project has any content.
 */
export const hasContent = (project: Project): boolean => {
  const data = project.jsonData;
  if (isWhiteboardData(data)) {
    return (data.elements?.length ?? 0) > 0;
  }
  if (isTextDocData(data)) {
    return data.content != null;
  }
  return (data.nodes?.length ?? 0) > 0;
};

/**
 * Counts the number of elements/nodes in a project.
 */
export const countProjectItems = (project: Project): number => {
  const data = project.jsonData;
  if (isWhiteboardData(data)) {
    return data.elements?.length ?? 0;
  }
  if (isTextDocData(data)) {
    return data.content != null ? 1 : 0;
  }
  return countMindmapNodes(data.nodes ?? []);
};

/**
 * Recursively counts all nodes in a mindmap tree.
 */
export const countMindmapNodes = (nodes: readonly import('../models/project.js').MindmapNode[]): number =>
  nodes.reduce((count, node) => {
    const childCount = node.children ? countMindmapNodes(node.children) : 0;
    return count + 1 + childCount;
  }, 0);

/**
 * Checks if a project belongs to a specific folder.
 */
export const isInFolder = (project: Project, folderId: string | null): boolean =>
  project.folderId === folderId;

/**
 * Checks if a project is at the root level (no folder).
 */
export const isAtRoot = (project: Project): boolean =>
  project.folderId === null;
